import type {AnalyticsConfig} from 'bitmovin-player';

import type {AdapterAPI} from '../../api/AdapterAPI';
import {Analytics} from '../../core/Analytics';
import type {CustomDataValues} from '../../types/CustomDataValues';
import {guardAgainstDuplicatedUserId, sanitizeAnalyticsConfig} from '../../utils/analyticsConfigUtils';
import {areCollectorRequirementsValid} from '../../utils/collectorRequirementChecks';
import {VERSION} from '../../utils/Version';

import {BitmovinPwxInternalAdapter} from './BitmovinPwxInternalAdapter';
import type {AnalyticsContext} from './Types';

export class BitmovinPwxAdapter implements AdapterAPI {
  private readonly internalAdapter: BitmovinPwxInternalAdapter;
  private readonly analytics: Analytics;

  constructor(context: AnalyticsContext, config: AnalyticsConfig) {
    // no need to check for player augmentation, if analytics has already been initialized on this player instance,
    // because this adapter is created only once with BitmovinPwxAnalyticsPackage

    const sanitizedConfig = sanitizeAnalyticsConfig(config);
    if (!areCollectorRequirementsValid(sanitizedConfig)) {
      throw new Error('Failed to create BitmovinPwxAdapter: domain is required');
    }

    guardAgainstDuplicatedUserId(sanitizedConfig);

    this.internalAdapter = new BitmovinPwxInternalAdapter(context);
    this.analytics = Analytics.create(sanitizedConfig, this.internalAdapter);
  }

  dispose() {
    this.internalAdapter.release();
  }

  static readonly version: string = VERSION;

  get version(): string {
    return VERSION;
  }

  getCurrentImpressionId = () => this.analytics.getCurrentImpressionId();

  getUserId = () => this.analytics.getUserId();

  setCustomData = (values: CustomDataValues) => this.internalAdapter.setCustomData(values);

  setCustomDataOnce = (values: CustomDataValues) => this.analytics.setCustomDataOnce(values);

  sourceChange = (_config: AnalyticsConfig) => {
    // Every time a source is added, a new instance of analytics package is triggered. So technically there are
    // no source changes. Also, a single player instance can handle infinite amount of sources at once.
    //
    // We are aware, at the moment we are handling it as "every source = one session". We will need to do
    // some additional work in the future (once we figure out how this part should work) to handle multiple
    // sources in one session (e.g. ads).
    //
    // What we mean by "no source changes", is that you do not have API to change "current playing" source
    // to something else. You would need to add new source and remove old one (or keep it). One instance can
    // have infinite amount of sources, each source for us is a new analytics session (for now, in certain cases
    // they should be tied together - e.g. ads)
    //
    // @see https://github.com/bitmovin-engineering/bitmovin-analytics-collector/pull/867#discussion_r1660699646

    throw new Error('Method not implemented.');
  };
}
